import { useNavigate } from "react-router-dom";

import {
  Text,
  Paper,
  Badge,
  Group,
  Stack,
  Title,
  Grid as MantineGrid,
  Card,
  Button,
  Box,
} from "@mantine/core";
import { IconBook } from "@tabler/icons-react";
import { DatePicker } from "@mantine/dates";
import {
  Container,
  Grid2 as Grid,
  Grid2Props as GridProps,
} from "@mui/material";

import "./styles.scss";
import AnimateOnLoad from "../../Components/AnimateOnLoad";
import { RouteList } from "../../Lib/routes";
import { usePerformRequest } from "../../Lib/usePerformRequest";
import { NonPaginatedResponse } from "../../Lib/responses";
import { Endpoints } from "../../Lib/endpoints";
import { CourseProps, LessonProps } from "../../Lib/types";
import { motion } from "framer-motion";
import dayjs from "dayjs";
import { useState } from "react";
import { Colors } from "../../Lib/data";
import MegaLoader from "../../Components/MegaLoader";

export default function Overview() {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());

  const gridItemProps: GridProps = {
    className: "item",
    size: {
      xl: 2.4,
      lg: 2.4,
      md: 3,
      sm: 4,
      xs: 6,
    },
  };

  const { data: calendarData, isLoading: isLoadingCalendar } =
    usePerformRequest<
      NonPaginatedResponse<{
        courses: CourseProps[];
        lessons: LessonProps[];
      }>
    >({
      method: Endpoints.GetCalendar().method,
      route: Endpoints.GetCalendar().route,
    });

  // Group lessons by date - using createdAt as lesson start time
  // TODO: When initialRuntime or runTimes data becomes available, update this logic
  const lessonsByDate = new Map<string, LessonProps[]>();

  if (calendarData?.data?.lessons) {
    calendarData.data.lessons.forEach((lesson) => {
      // Use createdAt as the lesson start time for now
      // When initialRuntime or runTimes data is available, use that instead
      const lessonStartTime = lesson.initialRunTime; // TODO: Replace with lesson.initialRuntime or lesson.runTimes[0]?.startTime
      const dateKey = dayjs(lessonStartTime).format("YYYY-MM-DD");

      if (!lessonsByDate.has(dateKey)) {
        lessonsByDate.set(dateKey, []);
      }
      lessonsByDate.get(dateKey)!.push(lesson);
    });
  }

  // Get lessons for selected date
  const selectedDateKey = selectedDate
    ? dayjs(selectedDate).format("YYYY-MM-DD")
    : null;
  const lessonsForSelectedDate = selectedDateKey
    ? lessonsByDate.get(selectedDateKey) || []
    : [];

  // Get upcoming lessons (next 30 days) for the sidebar
  const upcomingLessons = Array.from(lessonsByDate.entries())
    .filter(([dateKey]) => {
      const lessonDate = dayjs(dateKey);
      const today = dayjs();
      return lessonDate.isAfter(today) || lessonDate.isSame(today, "day");
    })
    .sort(([a], [b]) => dayjs(a).diff(dayjs(b)))
    .slice(0, 10);

  // Custom render function for calendar days
  const renderDay = (date: Date) => {
    const dateKey = dayjs(date).format("YYYY-MM-DD");
    const lessonsOnDate = lessonsByDate.get(dateKey);
    const isSelected = selectedDate && dayjs(date).isSame(selectedDate, "day");
    const isToday = dayjs(date).isSame(dayjs(), "day");
    const hasLessons = lessonsOnDate && lessonsOnDate.length > 0;

    return (
      <Box
        style={{
          position: "relative",
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "4px",
          background: isSelected
            ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
            : hasLessons
            ? `linear-gradient(135deg, ${Colors.PurplePrimary} 0%, #a084e8 100%)`
            : isToday
            ? "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)"
            : "transparent",
          color: isSelected || hasLessons || isToday ? "white" : "inherit",
          fontWeight: isSelected || hasLessons || isToday ? 700 : 500,
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          cursor: "pointer",
          minHeight: "40px",
          boxShadow:
            isSelected || hasLessons || isToday
              ? "0 8px 25px rgba(0,0,0,0.15)"
              : "none",
          transform:
            isSelected || hasLessons || isToday ? "scale(1.05)" : "scale(1)",
          "&:hover": {
            transform: "scale(1.1)",
            boxShadow: "0 12px 35px rgba(0,0,0,0.2)",
          },
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "scale(1.1)";
          e.currentTarget.style.boxShadow = "0 12px 35px rgba(0,0,0,0.2)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform =
            isSelected || hasLessons || isToday ? "scale(1.05)" : "scale(1)";
          e.currentTarget.style.boxShadow =
            isSelected || hasLessons || isToday
              ? "0 8px 25px rgba(0,0,0,0.15)"
              : "none";
        }}
      >
        <Text
          size="sm"
          fw={isSelected || hasLessons || isToday ? 700 : 500}
          style={{
            textShadow:
              isSelected || hasLessons || isToday
                ? "0 1px 3px rgba(0,0,0,0.3)"
                : "none",
          }}
        >
          {date.getDate()}
        </Text>
      </Box>
    );
  };

  console.log(calendarData);
  return (
    <AnimateOnLoad>
      {isLoadingCalendar ? (
        <>
          <MegaLoader />
        </>
      ) : (
        <div className="overview-container flex-row justify-center width-100">
          <Container maxWidth="lg">
            <div className="component-header">
              <Text className="label" tt="capitalize">
                Overview
              </Text>
            </div>
            <br />

            {/* Lesson Schedule Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Paper
                radius="4px"
                p="xl"
                shadow="xl"
                className="calendar-section"
                mb="xl"
                style={{
                  background:
                    "linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)",
                  backdropFilter: "blur(20px)",
                  border: "1px solid rgba(255,255,255,0.3)",
                  boxShadow:
                    "0 25px 50px rgba(0,0,0,0.1), 0 0 0 1px rgba(255,255,255,0.1)",
                }}
              >
                <Stack gap="xl">
                  <Group justify="space-between" align="center">
                    <div>
                      <Title
                        order={2}
                        style={{
                          background:
                            "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                          backgroundClip: "text",
                          fontWeight: 800,
                          fontSize: "2.5rem",
                          marginBottom: "8px",
                        }}
                      >
                        Lesson Schedule
                      </Title>
                      <Text size="lg" c="dimmed" style={{ fontWeight: 500 }}>
                        Select a date to view your lessons
                      </Text>
                    </div>
                    <Badge
                      size="xl"
                      style={{
                        background:
                          "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        color: "white",
                        fontWeight: 700,
                        padding: "12px 20px",
                        borderRadius: "4px",
                        fontSize: "1rem",
                      }}
                    >
                      {calendarData?.data?.lessons?.length || 0} Total Lessons
                    </Badge>
                  </Group>

                  <MantineGrid gutter="xl">
                    {/* DatePicker Column */}
                    <MantineGrid.Col span={{ base: 12, md: 4 }}>
                      <Stack gap="lg">
                        <Stack gap="md">
                          <Text
                            size="lg"
                            fw={700}
                            style={{
                              background:
                                "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                              WebkitBackgroundClip: "text",
                              WebkitTextFillColor: "transparent",
                              backgroundClip: "text",
                            }}
                          >
                            Select Date
                          </Text>
                          <Paper
                            p="xl"
                            radius="4px"
                            withBorder
                            style={{
                              background:
                                "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.85) 100%)",
                              backdropFilter: "blur(15px)",
                              border: "1px solid rgba(255,255,255,0.4)",
                              boxShadow:
                                "0 20px 40px rgba(0,0,0,0.08), 0 0 0 1px rgba(255,255,255,0.2)",
                            }}
                          >
                            <DatePicker
                              value={selectedDate}
                              onChange={setSelectedDate}
                              renderDay={renderDay}
                            />
                          </Paper>
                        </Stack>

                        {/* Legend */}
                        <Paper
                          p="lg"
                          radius="4px"
                          style={{
                            background:
                              "linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)",
                            backdropFilter: "blur(10px)",
                            border: "1px solid rgba(255,255,255,0.3)",
                          }}
                        >
                          <Stack gap="md">
                            <Text
                              size="md"
                              fw={700}
                              style={{
                                background:
                                  "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                                backgroundClip: "text",
                              }}
                            >
                              Legend
                            </Text>
                            <Stack gap="sm">
                              <Group gap="md">
                                <Box
                                  style={{
                                    width: 20,
                                    height: 20,
                                    borderRadius: "4px",
                                    background: `linear-gradient(135deg, ${Colors.PurplePrimary} 0%, #a084e8 100%)`,
                                    boxShadow:
                                      "0 4px 12px rgba(240, 147, 251, 0.4)",
                                  }}
                                />
                                <Text size="sm" fw={600}>
                                  Has lessons
                                </Text>
                              </Group>
                              <Group gap="md">
                                <Box
                                  style={{
                                    width: 20,
                                    height: 20,
                                    borderRadius: "4px",
                                    background:
                                      "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                                    boxShadow:
                                      "0 4px 12px rgba(79, 172, 254, 0.4)",
                                  }}
                                />
                                <Text size="sm" fw={600}>
                                  Today
                                </Text>
                              </Group>
                              <Group gap="md">
                                <Box
                                  style={{
                                    width: 20,
                                    height: 20,
                                    borderRadius: "4px",
                                    background:
                                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                    boxShadow:
                                      "0 4px 12px rgba(102, 126, 234, 0.4)",
                                  }}
                                />
                                <Text size="sm" fw={600}>
                                  Selected
                                </Text>
                              </Group>
                            </Stack>
                          </Stack>
                        </Paper>

                        {/* Quick Date Buttons */}
                        <Stack gap="md">
                          <Text
                            size="lg"
                            fw={700}
                            style={{
                              background:
                                "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                              WebkitBackgroundClip: "text",
                              WebkitTextFillColor: "transparent",
                              backgroundClip: "text",
                            }}
                          >
                            Quick Select
                          </Text>
                          <Group gap="sm">
                            <Button
                              size="sm"
                              style={{
                                background:
                                  "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                color: "white",
                                fontWeight: 600,
                                borderRadius: "4px",
                                border: "none",
                                boxShadow:
                                  "0 4px 12px rgba(102, 126, 234, 0.3)",
                                transition: "all 0.3s ease",
                                "&:hover": {
                                  transform: "translateY(-2px)",
                                  boxShadow:
                                    "0 8px 20px rgba(102, 126, 234, 0.4)",
                                },
                              }}
                              onClick={() => setSelectedDate(new Date())}
                            >
                              Today
                            </Button>
                            <Button
                              size="sm"
                              style={{
                                background:
                                  "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                color: "white",
                                fontWeight: 600,
                                borderRadius: "4px",
                                border: "none",
                                boxShadow:
                                  "0 4px 12px rgba(102, 126, 234, 0.3)",
                                transition: "all 0.3s ease",
                                "&:hover": {
                                  transform: "translateY(-2px)",
                                  boxShadow:
                                    "0 8px 20px rgba(102, 126, 234, 0.4)",
                                },
                              }}
                              onClick={() =>
                                setSelectedDate(dayjs().add(1, "day").toDate())
                              }
                            >
                              Tomorrow
                            </Button>
                            <Button
                              size="sm"
                              style={{
                                background:
                                  "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                                color: "white",
                                fontWeight: 600,
                                borderRadius: "4px",
                                border: "none",
                                boxShadow: "0 4px 12px rgba(79, 172, 254, 0.3)",
                                transition: "all 0.3s ease",
                                "&:hover": {
                                  transform: "translateY(-2px)",
                                  boxShadow:
                                    "0 8px 20px rgba(79, 172, 254, 0.4)",
                                },
                              }}
                              onClick={() =>
                                setSelectedDate(dayjs().add(7, "day").toDate())
                              }
                            >
                              Next Week
                            </Button>
                          </Group>
                        </Stack>

                        {/* Upcoming Lessons Preview */}
                        <Paper
                          p="lg"
                          radius="4px"
                          style={{
                            background:
                              "linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)",
                            backdropFilter: "blur(10px)",
                            border: "1px solid rgba(255,255,255,0.3)",
                          }}
                        >
                          <Text
                            fw={700}
                            mb="md"
                            size="lg"
                            style={{
                              background:
                                "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                              WebkitBackgroundClip: "text",
                              WebkitTextFillColor: "transparent",
                              backgroundClip: "text",
                            }}
                          >
                            Upcoming Lessons
                          </Text>
                          <Stack gap="sm">
                            {upcomingLessons
                              .slice(0, 5)
                              .map(([dateKey, lessons]) => (
                                <Paper
                                  key={dateKey}
                                  p="sm"
                                  radius="4px"
                                  style={{
                                    cursor: "pointer",
                                    background: dayjs(dateKey).isSame(
                                      selectedDate,
                                      "day"
                                    )
                                      ? "linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)"
                                      : "linear-gradient(135deg, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0.6) 100%)",
                                    border: dayjs(dateKey).isSame(
                                      selectedDate,
                                      "day"
                                    )
                                      ? "1px solid rgba(102, 126, 234, 0.3)"
                                      : "1px solid rgba(255,255,255,0.2)",
                                    backdropFilter: "blur(10px)",
                                    transition: "all 0.3s ease",
                                    "&:hover": {
                                      transform: "translateY(-2px)",
                                      boxShadow: "0 8px 20px rgba(0,0,0,0.1)",
                                    },
                                  }}
                                  onClick={() =>
                                    setSelectedDate(dayjs(dateKey).toDate())
                                  }
                                >
                                  <Group justify="space-between">
                                    <Text size="sm" fw={600}>
                                      {dayjs(dateKey).format("MMM DD")}
                                    </Text>
                                    <Badge
                                      size="sm"
                                      style={{
                                        background: `linear-gradient(135deg, ${Colors.PurplePrimary} 0%, #a084e8 100%)`,
                                        color: "white",
                                        fontWeight: 600,
                                        borderRadius: "4px",
                                      }}
                                    >
                                      {lessons.length}
                                    </Badge>
                                  </Group>
                                </Paper>
                              ))}
                          </Stack>
                        </Paper>
                      </Stack>
                    </MantineGrid.Col>

                    {/* Lessons List Column */}
                    <MantineGrid.Col span={{ base: 12, md: 8 }}>
                      <Stack gap="lg">
                        <Group justify="space-between">
                          <Text
                            fw={700}
                            size="xl"
                            style={{
                              background:
                                "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                              WebkitBackgroundClip: "text",
                              WebkitTextFillColor: "transparent",
                              backgroundClip: "text",
                            }}
                          >
                            {selectedDate
                              ? dayjs(selectedDate).format("MMMM DD, YYYY")
                              : "Select a date"}
                          </Text>
                          {lessonsForSelectedDate.length > 0 && (
                            <Badge
                              style={{
                                background: `linear-gradient(135deg, ${Colors.PurplePrimary} 0%, #a084e8 100%)`,
                                color: "white",
                                fontWeight: 700,
                                padding: "8px 16px",
                                borderRadius: "4px",
                                fontSize: "0.9rem",
                              }}
                            >
                              {lessonsForSelectedDate.length} lesson
                              {lessonsForSelectedDate.length > 1 ? "s" : ""}
                            </Badge>
                          )}
                        </Group>

                        {lessonsForSelectedDate.length > 0 ? (
                          <Stack gap="lg">
                            {lessonsForSelectedDate.map((lesson, index) => (
                              <motion.div
                                key={lesson.id}
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{
                                  duration: 0.4,
                                  delay: index * 0.1,
                                }}
                              >
                                <Card
                                  shadow="xl"
                                  padding="xl"
                                  radius="4px"
                                  style={{
                                    background:
                                      "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.85) 100%)",
                                    backdropFilter: "blur(15px)",
                                    border: "1px solid rgba(255,255,255,0.3)",
                                    boxShadow:
                                      "0 20px 40px rgba(0,0,0,0.08), 0 0 0 1px rgba(255,255,255,0.2)",
                                  }}
                                >
                                  <Stack gap="md">
                                    <Group justify="space-between">
                                      <Text
                                        fw={700}
                                        size="xl"
                                        style={{
                                          background:
                                            "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                          WebkitBackgroundClip: "text",
                                          WebkitTextFillColor: "transparent",
                                          backgroundClip: "text",
                                        }}
                                      >
                                        {lesson.title}
                                      </Text>
                                      <Badge
                                        style={{
                                          background:
                                            "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                                          color: "white",
                                          fontWeight: 600,
                                          padding: "8px 16px",
                                          borderRadius: "4px",
                                          fontSize: "0.9rem",
                                        }}
                                      >
                                        Scheduled
                                      </Badge>
                                    </Group>

                                    <Text
                                      size="md"
                                      c="dimmed"
                                      lineClamp={2}
                                      style={{ fontWeight: 500 }}
                                    >
                                      {lesson.description}
                                    </Text>

                                    <Group gap="md">
                                      <Text
                                        size="sm"
                                        c="dimmed"
                                        style={{ fontWeight: 500 }}
                                      >
                                        Start Time:{" "}
                                        {dayjs(lesson.createdAt).format(
                                          "MMM DD, YYYY h:mm A"
                                        )}
                                      </Text>
                                      {lesson.updatedAt && (
                                        <Text
                                          size="sm"
                                          c="dimmed"
                                          style={{ fontWeight: 500 }}
                                        >
                                          Updated:{" "}
                                          {dayjs(lesson.updatedAt).format(
                                            "MMM DD, YYYY"
                                          )}
                                        </Text>
                                      )}
                                    </Group>
                                  </Stack>
                                </Card>
                              </motion.div>
                            ))}
                          </Stack>
                        ) : (
                          <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.5 }}
                          >
                            <Paper
                              p="xl"
                              radius="4px"
                              style={{
                                minHeight: 300,
                                background:
                                  "linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)",
                                backdropFilter: "blur(15px)",
                                border: "1px solid rgba(255,255,255,0.3)",
                              }}
                            >
                              <Stack
                                align="center"
                                justify="center"
                                gap="lg"
                                h="100%"
                              >
                                <div
                                  style={{
                                    width: 120,
                                    height: 120,
                                    borderRadius: "4px",
                                    background:
                                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    boxShadow:
                                      "0 20px 40px rgba(102, 126, 234, 0.3)",
                                  }}
                                >
                                  <IconBook size={48} color="white" />
                                </div>
                                <div style={{ textAlign: "center" }}>
                                  <Text
                                    size="xl"
                                    fw={700}
                                    mb="md"
                                    style={{
                                      background:
                                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                      WebkitBackgroundClip: "text",
                                      WebkitTextFillColor: "transparent",
                                      backgroundClip: "text",
                                    }}
                                  >
                                    No Lessons Scheduled
                                  </Text>
                                  <Text
                                    size="lg"
                                    c="dimmed"
                                    maw={400}
                                    style={{ fontWeight: 500 }}
                                  >
                                    {selectedDate
                                      ? `You don't have any lessons scheduled for ${dayjs(
                                          selectedDate
                                        ).format("MMMM DD, YYYY")}.`
                                      : "Select a date to view your lesson schedule."}
                                  </Text>
                                </div>
                              </Stack>
                            </Paper>
                          </motion.div>
                        )}
                      </Stack>
                    </MantineGrid.Col>
                  </MantineGrid>
                </Stack>
              </Paper>
            </motion.div>

            <Grid container className="items">
              {RouteList.map((route) => {
                return (
                  <Grid
                    key={route.label}
                    {...gridItemProps}
                    // @ts-ignore
                    onClick={() => {
                      navigate(`${route.path}`);
                    }}
                  >
                    <route.icon
                      size={40}
                      stroke={1.2}
                      color="var(--purple-primary)"
                    />
                    <Text className="label">{route.label}</Text>
                  </Grid>
                );
              })}
            </Grid>
          </Container>
        </div>
      )}
    </AnimateOnLoad>
  );
}
